import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { Observable, of, switchMap, map, catchError } from 'rxjs';
import { ProjectSelectorComponent } from './components/project-selector/project-selector.component';
import { FeatureFlagTableComponent } from './components/feature-flag-table/feature-flag-table.component';
import {
  LaunchDarklyService,
} from './services/launch-darkly.service';
import { LoadingService } from 'src/app/services/loading.service';
import { FeatureFlag, Environment, LdProject } from './models/feature-flag-models';

@Component({
  selector: 'app-feature-flag-manager',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatSelectModule,
    MatTableModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ProjectSelectorComponent,
    FeatureFlagTableComponent,
  ],
  template: `
    <mat-card>
      <mat-card-header>
        <mat-card-title>Feature Flag Manager</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="api-key-container">
          <mat-form-field class="api-key-field">
            <mat-label>LaunchDarkly API Key</mat-label>
            <input
              matInput
              type="password"
              [(ngModel)]="apiKey"
              (ngModelChange)="onApiKeyChange($event)"
              placeholder="Enter your LaunchDarkly API key"
              />
            </mat-form-field>
          </div>
    
          <div class="controls-row">
            <app-project-selector
              [projects]="projects$ | async"
              [disabled]="!apiKey"
              (projectSelected)="onProjectSelected($event)"
            ></app-project-selector>

            @if (selectedProject) {
              <app-feature-flag-table
                [featureFlags]="featureFlags$ | async"
                [loading]="(loadingService.loading$ | async) ?? false"
                [apiKey]="apiKey"
                [projectKey]="selectedProject.key"
                (toggleFlag)="onToggleFlag($event)"
              ></app-feature-flag-table>
            }
          </div>
        </mat-card-content>
      </mat-card>
    `,
  styles: [
    `
      mat-card {
        margin: 20px;
      }
      mat-card-content {
        margin-top: 20px;
      }
      .api-key-container {
        margin-bottom: 20px;
      }
      .api-key-field {
        width: 100%;
        max-width: 500px;
      }
      .controls-row {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
      }
      .controls-row app-project-selector {
        /* flex-basis gives the mat-form-field's width:100% a definite size to
           resolve against, matching the API key field width instead of
           shrink-to-fit collapsing it */
        flex: 0 1 500px;
      }
      /* app-feature-flag-table's :host is display:contents specifically so its
         search field and table become direct children of this flex row - see
         that component's :host comment for the full explanation. */
    `,
  ],
})
export class FeatureFlagManagerComponent implements OnInit {
  private readonly launchDarklyService = inject(LaunchDarklyService);
  public readonly loadingService = inject(LoadingService);

  selectedProject: LdProject | null = null;
  projects$: Observable<LdProject[]> = of([]);
  featureFlags$: Observable<FeatureFlag[]> = of([]);
  private environments: Environment[] = [];
  apiKey: string = '';

  ngOnInit(): void {
    // Projects will be loaded when API key is entered
  }

  onApiKeyChange(apiKey: string) {
    this.apiKey = apiKey;
    if (apiKey) {
      this.projects$ = this.launchDarklyService.getProjects(apiKey);
    } else {
      this.projects$ = of([]);
      this.selectedProject = null;
      this.featureFlags$ = of([]);
    }
  }

  onProjectSelected(project: LdProject): void {
    this.loadingService.show('projectSelector');
    this.selectedProject = project;

    // First load environments
    this.launchDarklyService
      .loadEnvironments(this.apiKey, project.key)
      .pipe(
        map((environments) => {
          this.environments = environments;
          return project.key;
        }),
        switchMap((projectKey) =>
          this.launchDarklyService.getFeatureFlags(this.apiKey, projectKey)
        ),
        map((flags) => {
          this.loadingService.hide('projectSelector');
          return flags;
        }),
        catchError((error) => {
          this.loadingService.hide('projectSelector');
          console.error('Error loading feature flags:', error);
          return of([]);
        })
      )
      .subscribe((flags) => {
        this.featureFlags$ = of(flags);
      });
  }

  onToggleFlag(data: {
    flagKey: string;
    environment: string;
    enabled: boolean;
  }): void {
    if (!this.selectedProject) return;

    this.loadingService.show('featureFlags');
    this.launchDarklyService
      .toggleFeatureFlag(
        this.apiKey,
        this.selectedProject.key,
        data.flagKey,
        data.environment,
        data.enabled
      )
      .pipe(
        switchMap(() =>
          this.launchDarklyService.getFeatureFlags(
            this.apiKey,
            this.selectedProject!.key
          )
        ),
        map((flags) => {
          this.loadingService.hide('featureFlags');
          return flags;
        }),
        catchError((error) => {
          this.loadingService.hide('featureFlags');
          console.error('Error toggling feature flag:', error);
          return of([]);
        })
      )
      .subscribe((flags) => {
        this.featureFlags$ = of(flags);
      });
  }
}
